import { useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  UserCog,
  MessageCircle,
  BarChart3,
  Calendar,
  Calculator,
  Settings,
  LogOut,
  HelpCircle,
} from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { APP_VERSION } from "@/lib/version";

const NAV_ITEMS = [
  { to: "/", label: "Tableau de bord", icon: LayoutDashboard, exact: true },
  { to: "/leads", label: "Leads", icon: Users },
  { to: "/messages", label: "Messages", icon: MessageCircle },
  { to: "/calendrier", label: "Calendrier", icon: Calendar },
  { to: "/rapports", label: "Rapports", icon: BarChart3 },
  { to: "/simulation", label: "Simulation", icon: Calculator },
  { to: "/utilisateurs", label: "Utilisateurs", icon: UserCog, adminOnly: true },
  { to: "/parametres", label: "Paramètres", icon: Settings },
  { to: "/aide", label: "Aide", icon: HelpCircle },
];

export default function MobileDrawer({ open, onOpenChange }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { companyName } = useSettings();

  const isActive = (item) =>
    item.exact ? location.pathname === item.to : location.pathname.startsWith(item.to);

  const go = (to) => {
    navigate(to);
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="left" className="w-[80vw] max-w-[320px] p-0">
        <SheetHeader className="border-b border-border/60 px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-border/60 bg-white shadow-soft">
              <img src="/sogi-logo.png" alt="SOGI" className="h-7 w-7 object-contain" />
            </div>
            <SheetTitle className="font-heading flex-1 text-left text-base font-bold tracking-tight">
              {companyName || "SOGI SARL"}
              <div className="text-[10px] font-normal uppercase tracking-[0.15em] text-muted-foreground">
                CRM Immobilier
              </div>
            </SheetTitle>
          </div>
        </SheetHeader>

        <nav className="flex-1 space-y-1 p-3">
          {NAV_ITEMS.filter((it) => !it.adminOnly || user?.role === "admin").map((item) => {
            const Icon = item.icon;
            const active = isActive(item);
            return (
              <button
                key={item.to}
                onClick={() => go(item.to)}
                data-testid={`mobile-nav-${item.to.replace(/\//g, "") || "home"}`}
                className={cn(
                  "flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium",
                  active
                    ? "bg-accent-navy text-white"
                    : "hover:bg-muted"
                )}
              >
                <Icon className="h-[18px] w-[18px]" strokeWidth={active ? 2.5 : 2} />
                {item.label}
              </button>
            );
          })}
        </nav>

        <div className="border-t border-border/60 p-3">
          <div className="flex items-center gap-2 rounded-lg bg-muted/40 px-3 py-2.5">
            <div className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-accent-navy text-xs font-bold text-white">
              {(user?.email || "A")[0].toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <span className="truncate text-xs font-semibold">Admin</span>
                <span
                  className="rounded-full bg-accent-navy/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-accent-navy dark:bg-accent-gold/20 dark:text-accent-gold"
                  title={`Version ${APP_VERSION}`}
                >
                  v{APP_VERSION}
                </span>
              </div>
              <div className="truncate text-[10px] text-muted-foreground">
                {user?.email || "—"}
              </div>
            </div>
            <button
              onClick={async () => {
                await logout();
                onOpenChange(false);
                navigate("/login");
              }}
              data-testid="mobile-drawer-logout"
              className="flex h-9 w-9 flex-none items-center justify-center rounded-md hover:bg-muted"
              title="Déconnexion"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
